import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { ContractDto } from "@rentablo/api/contracts";
import {
  ContractFormValues,
  ContractSubCategory,
} from "@features/contractAnalyzer/types";
import { getContractCategories } from "@features/contractAnalyzer/utils";
import {
  CategoryReturn,
  ContractsTab,
  View,
} from "@features/contractAnalyzer/edit/view";
import { ContractEditTarget } from "../ContractAnalyzerProvider";

const providerNameOf = (contract: ContractDto | undefined) =>
  contract?.userDefinedProvider ?? contract?.autoAssignedProvider?.name ?? "";

const initialViewFor = (target: ContractEditTarget): View => {
  if (target.initialMode === "categoryOnly") return { name: "categoryOnly" };
  if (target.initialMode === "splitIntoNew")
    return { name: "split", step: "main" };
  return { name: "overview" };
};

interface Params {
  target: ContractEditTarget;
  contract: ContractDto;
  secondContract: ContractDto | undefined;
  setSubCategory: (next: ContractSubCategory) => void;
  setSplitSelection: (next: string[]) => void;
  setSecondContract: (next: ContractDto | undefined) => void;
  secondContractForm: UseFormReturn<ContractFormValues>;
  onClose: () => void;
}

export const useEditNavigation = ({
  target,
  contract,
  secondContract,
  setSubCategory,
  setSplitSelection,
  setSecondContract,
  secondContractForm,
  onClose,
}: Params) => {
  const [view, setView] = useState<View>(() => initialViewFor(target));
  const [tab, setTab] = useState<ContractsTab>("details");

  const openCategoryPicker = (returnTo: CategoryReturn) =>
    setView({ name: "category", returnTo });

  const openSplit = () => {
    setSplitSelection([]);
    setSubCategory("notClassified");
    secondContractForm.reset({ provider: providerNameOf(contract) });
    setView({ name: "split", step: "main" });
  };

  const openMerge = () => {
    setSecondContract(undefined);
    setSubCategory(getContractCategories(contract).subCategory);
    secondContractForm.reset({ provider: "" });
    setView({ name: "merge", step: "main" });
  };

  const openAlarm = () => setView({ name: "alarm" });

  const goBack = () => {
    switch (view.name) {
      case "overview":
      case "categoryOnly":
        return onClose();
      case "category":
        return setView(view.returnTo);
      case "alarm":
        setTab("manage");
        setView({ name: "overview" });
        return;
      case "split":
        if (view.step === "confirm") {
          setView({ name: "split", step: "main" });
        } else {
          setSplitSelection([]);
          setTab("manage");
          setView({ name: "overview" });
        }
        return;
      case "merge":
        if (view.step === "main") {
          if (secondContract) {
            setSecondContract(undefined);
            setSubCategory(getContractCategories(contract).subCategory);
            secondContractForm.reset({ provider: "" });
            return;
          }
          setSubCategory(getContractCategories(contract).subCategory);
          secondContractForm.reset({ provider: "" });
          setTab("manage");
          setView({ name: "overview" });
        } else if (view.step === "selectContract") {
          setSecondContract(undefined);
          setView({ name: "merge", step: "main" });
        } else {
          setView({ name: "merge", step: "main" });
        }
        return;
    }
  };

  const handleCategorySelect = (next: ContractSubCategory) => {
    setSubCategory(next);
    if (view.name === "category") setView(view.returnTo);
  };

  const handleCategoryCancel = () => {
    if (view.name === "category") setView(view.returnTo);
  };

  const handleSplitAdvance = () => {
    if (view.name !== "split" || view.step !== "main") return;
    setView({ name: "split", step: "confirm" });
  };

  const handleMergeAdvance = () => {
    if (view.name !== "merge") return;
    if (view.step === "main") setView({ name: "merge", step: "confirm" });
  };

  return {
    view,
    tab,
    setTab,
    setView,
    openCategoryPicker,
    openSplit,
    openMerge,
    openAlarm,
    goBack,
    handleCategorySelect,
    handleCategoryCancel,
    handleSplitAdvance,
    handleMergeAdvance,
  };
};
